import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { DashboardHeader, StatCard, Tabs, StatusBadge, Empty, fmtTs, fmtTime12 } from "../components/DashboardUI";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { downloadCustomerReceipt } from "../lib/pdf";

export default function StaffDashboard() {
  const { user, logout, loading } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [b, c, s] = await Promise.all([api.get("/bookings"), api.get("/staff/customers"), api.get("/staff/stats")]);
      setBookings(b.data); setCustomers(c.data); setStats(s.data);
    } catch { nav("/staff/login"); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/staff/login");
    if (user.role !== "agent") return nav("/");
    load();
  }, [user, loading]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="staff-dashboard">
      <DashboardHeader title="Salon Staff" user={user} onLogout={logout} onChangePassword={() => setPwdOpen(true)} />
      {stats && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Customers" value={stats.customers} />
          <StatCard label="Pending Bookings" value={stats.bookings_pending} accent />
          <StatCard label="Total Bookings" value={stats.bookings_today_or_later} />
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-6">
        <Tabs current={tab} onChange={setTab} prefix="staff-tab" tabs={[
          { k: "bookings", label: "Bookings (View)" },
          { k: "customers", label: "Customers (View)" },
        ]} />
        <p className="text-xs text-[var(--muted)] italic mt-3">Read-only access. Reception can assign you to bookings.</p>
        <div className="mt-6 pb-16">
          {tab === "bookings" && <BookingsList items={bookings} userId={user.id} />}
          {tab === "customers" && <CustomersList items={customers} />}
        </div>
      </div>
      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
}

function BookingsList({ items, userId }) {
  if (!items.length) return <Empty msg="No bookings." />;
  return (
    <div className="bg-white border border-[var(--border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
          <tr><th className="text-left p-4">Guest</th><th className="text-left p-4">Service</th><th className="text-left p-4">Date / Time</th><th className="text-left p-4">Phone</th><th className="text-left p-4">Status</th><th className="text-left p-4">Assigned</th></tr>
        </thead>
        <tbody>
          {items.map(b => (
            <tr key={b.id} className={`border-t border-[var(--border)] ${b.assigned_staff_id === userId ? "bg-[#FFFBF0]" : ""}`} data-testid={`staff-booking-${b.id}`}>
              <td className="p-4">{b.name}{b.notes && <p className="text-xs text-[var(--muted)] italic mt-1">"{b.notes}"</p>}</td>
              <td className="p-4">{b.service}</td>
              <td className="p-4">{b.date}<br/><span className="text-xs text-[var(--muted)]">{b.time}</span></td>
              <td className="p-4 text-xs"><a href={`https://wa.me/${(b.phone||"").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="text-[var(--accent)] hover:underline">{b.phone}</a></td>
              <td className="p-4"><StatusBadge s={b.status} /></td>
              <td className="p-4 text-xs">{b.assigned_staff_name || <span className="text-[var(--muted)]">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomersList({ items }) {
  if (!items.length) return <Empty msg="No customers." />;
  return (
    <div className="bg-white border border-[var(--border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
          <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Phone</th><th className="text-left p-4">Email</th><th className="text-left p-4">Visits</th><th className="text-left p-4">Last Service</th><th className="text-right p-4">Report</th></tr>
        </thead>
        <tbody>
          {items.map(c => (
            <tr key={c.id} className="border-t border-[var(--border)]" data-testid={`staff-customer-${c.id}`}>
              <td className="p-4 font-medium">{c.name}</td>
              <td className="p-4">{c.phone}</td>
              <td className="p-4 text-xs">{c.email || <span className="text-[var(--muted)] italic">private</span>}</td>
              <td className="p-4">{c.bookings_count}</td>
              <td className="p-4 text-xs">{c.last_service ? `${c.last_service} · ${c.last_visit}` : <span className="text-[var(--muted)]">—</span>}</td>
              <td className="p-4 text-right">
                <button onClick={() => downloadCustomerReceipt(c.id, c.name)} className="text-xs px-3 py-1.5 border border-[var(--border)] hover:bg-[var(--bg)] rounded" data-testid={`pdf-${c.id}`}>
                  <i className="fa-regular fa-file-pdf mr-1"></i> PDF
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
